import { useMemo, type CSSProperties } from 'react';
import type { WidgetLayout } from '@/config/types';
import { parseStyleString } from '@/lib/parseStyleString';

/**
 * The base className every widget root carries.
 *
 * Taken from legacy `componentLayout()` in `utils/functions.ts`. Note that the legacy
 * widgets did **not** all agree with it: several of them hand-rolled
 * `flex w-full flex-col lg:flex-row`, dropping `component-column flex-wrap`, which meant
 * those widgets got no `gap` at `lg` and no wrapping. `componentLayout()` is treated as
 * canonical here — it is the version this ticket was specified against — and a widget that
 * genuinely needs the narrower set overrides it through its own props in SYM-495+.
 *
 * `component-column` is not a Tailwind utility; it is the gutter rule in `global.css`,
 * scoped to `.signage-container`.
 */
export const WIDGET_LAYOUT_BASE_CLASS =
  'flex component-column flex-wrap w-full flex-col lg:flex-row';

export type WidgetLayoutResult = {
  className: string;
  style: CSSProperties;
};

/**
 * `options.layout` -> a className and a style object, for the root element of a widget.
 *
 * Replaces legacy `componentLayout(options)`, which was additionally copy-pasted into
 * almost every widget. Every widget from SYM-491 onwards uses this hook and nothing else.
 *
 * ```tsx
 * const { className, style } = useWidgetLayout(options.layout);
 * return <div className={className} style={style}>…</div>;
 * ```
 *
 * Behaviour preserved verbatim from the legacy function:
 *
 * - `rounded` is appended to the className (`rounded-xl` in every deployed config —
 *   safelisted in `index.css`, since Tailwind never scans the JSON it comes from).
 * - `style`, then `backgroundColor`, then `padding` are concatenated into one declaration
 *   string. **The order is load-bearing**: later declarations overwrite earlier ones, so a
 *   `backgroundColor` beats a `background` inside `style`.
 * - `backgroundColor` maps to the `background` shorthand, not `background-color` — configs
 *   pass gradients through that key.
 * - Falsy values are skipped, including `padding: 0`. Fifteen deployed widgets carry
 *   `"padding": 0` and none of them renders a `padding: 0rem` declaration today.
 */
export function useWidgetLayout(layout: WidgetLayout | undefined): WidgetLayoutResult {
  const { rounded, style, backgroundColor, padding } = layout ?? {};

  // Memoised on the individual fields rather than on `layout`: the parsed config is
  // immutable so its `layout` object is referentially stable, but a caller that builds one
  // inline would otherwise get a fresh style object on every render and defeat the
  // memoisation of everything below it.
  return useMemo(() => {
    let className = WIDGET_LAYOUT_BASE_CLASS;
    if (rounded) className += ' ' + rounded;

    let declarations = '';
    if (style) declarations += terminate(style);
    if (backgroundColor) declarations += `background: ${backgroundColor};`;
    if (padding) declarations += `padding: ${String(padding)}rem;`;

    return { className, style: parseStyleString(declarations) };
  }, [rounded, style, backgroundColor, padding]);
}

/**
 * Appends the `;` that separates a config-supplied declaration string from the ones this
 * hook appends after it.
 *
 * The legacy code concatenated blindly, so a config whose `style` lacked a trailing `;`
 * would fuse its last declaration with the generated `background:` one and lose both. Every
 * deployed value ends with `;` already, so this changes nothing on any live display — it
 * only stops a hand-edit from blanking a widget. Recorded as MIGRATION decision #17.
 */
function terminate(declarations: string): string {
  return declarations.trimEnd().endsWith(';') ? declarations : declarations + ';';
}
